import { useCallback, useEffect, useMemo, useState } from "react";
import type { AcaoLinha, Refs, Row, TabDef } from "../types";
import { api, ApiError } from "../api";
import { formatCell, statusTone } from "../format";
import RecordForm from "./RecordForm";
import ErrorState from "./ErrorState";
import SugerirTreinosModal from "./SugerirTreinosModal";
import ConcluirTreinoModal from "./ConcluirTreinoModal";
import CertificadoModal from "./CertificadoModal";
import CertificadoCalibracaoModal from "./CertificadoCalibracaoModal";
import LiberacaoModal from "./LiberacaoModal";
import PastaModal from "./PastaModal";
import DocumentosVeiculoModal from "./DocumentosVeiculoModal";
import { FormEquipamento } from "./EquipamentosEnsaioView";
import { IconSearch, IconPlus, IconEdit, IconTrash, IconSparkles, IconFolder, IconCheck, IconCertificate, IconUnlock, IconRefresh } from "../icons";
import { usePodeExcluir } from "../permissoes";
import { ordenarPorNome } from "../ordem";

/** Metadados dos botões de ação extra por linha (definidos em tab.acoesLinha). */
const ACAO_META: Record<AcaoLinha, { title: string; Icon: typeof IconSparkles; cls: string }> = {
  // `modo-leitura-esconde` (styles.css): gravam, mas o título não diz "salvar/
  // editar" — sem a classe ficariam à vista de quem é somente visualização.
  treinoIa: { title: "Sugerir treinamentos (IA)", Icon: IconSparkles, cls: "icon-btn-ia modo-leitura-esconde" },
  pasta: { title: "Pasta do colaborador", Icon: IconFolder, cls: "icon-btn-folder" },
  concluir: { title: "Concluir treinamento", Icon: IconCheck, cls: "icon-btn-ok" },
  certificado: { title: "Anexar certificado (envia ao DP)", Icon: IconCertificate, cls: "icon-btn-cert" },
  liberar: { title: "Liberar / bloquear equipamento", Icon: IconUnlock, cls: "icon-btn-ok modo-leitura-esconde" },
  calibracao: { title: "Calibração: anexar certificado (PDF) ou informar a data", Icon: IconCertificate, cls: "icon-btn-cert" },
  documentos: { title: "Documentos do veículo (aparecem no Operacional)", Icon: IconFolder, cls: "icon-btn-folder" },
};

interface Props {
  tab: TabDef;
  refs: Refs;
  onDataChanged: () => void; // avisa o App para recarregar referências/dashboard
}

export default function CrudView({ tab, refs, onDataChanged }: Props) {
  // Excluir é só de administrador (regra do Portal): sem isso, a lixeira e o
  // botão de limpeza da Equipe nem aparecem.
  const podeExcluir = usePodeExcluir();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [acao, setAcao] = useState<{ tipo: AcaoLinha; row: Row } | null>(null);
  // Aba espelhada do RH/DP (Equipe): estado do botão "Sincronizar".
  const [sincronizando, setSincronizando] = useState(false);
  const [avisoSync, setAvisoSync] = useState<{ ok: boolean; texto: string } | null>(null);
  /**
   * Quem o RH/DP tem mas NÃO é equipe de campo (cozinheira, costureira,
   * vendedor, administrativo). Guardado e escondido: jogar 60 nomes desses no
   * aviso de sucesso fazia parecer que eles tinham ENTRADO neste sistema,
   * quando o certo é o contrário — eles ficaram de fora.
   */
  const [foraDaEquipe, setForaDaEquipe] = useState<{ nome: string; motivo: string }[]>([]);
  const [verForaDaEquipe, setVerForaDaEquipe] = useState(false);
  const [promovendo, setPromovendo] = useState(false);
  /** Quantos cadastros só existem aqui (viram o botão "Enviar ao RH/DP"). */
  const [pendentesRh, setPendentesRh] = useState(0);
  /** Quem a última sincronização apontou como "não está mais no RH/DP". */
  const [foraDaFonte, setForaDaFonte] = useState<{ id: number; nome: string | null }[]>([]);
  const [removendo, setRemovendo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.list(tab.resource));
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, [tab.resource]);

  useEffect(() => {
    setSearch("");
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const achados = !q
      ? rows
      : rows.filter((r) =>
          tab.fields.some((f) => {
            const value = f.ref
              ? f.ref === "obras"
                ? r.obra_nome
                : f.ref === "equipamentos"
                  ? r.equipamento_nome
                  : r.colaborador_nome
              : r[f.name];
            return String(value ?? "").toLowerCase().includes(q);
          }),
        );
    // Cadastro identificado por nome (Equipe, Usuários, Equipamentos, Veículos
    // pela placa): a tabela aparece em ordem alfabética A → Z. Aba de
    // lançamento não define `ordenarPor` e continua na ordem do servidor.
    const campo = tab.ordenarPor;
    return campo ? ordenarPorNome(achados, (r) => r[campo]) : achados;
  }, [rows, search, tab.fields, tab.ordenarPor]);

  /** Quem ficou de fora da equipe, também em ordem alfabética. */
  const foraDaEquipeOrdenado = useMemo(
    () => ordenarPorNome(foraDaEquipe, (p) => p.nome),
    [foraDaEquipe],
  );

  function openNew() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(row: Row) {
    setEditing(row);
    setFormOpen(true);
  }

  async function handleSubmit(data: Row) {
    if (tab.fonteRh && editing) {
      // Aba Equipe: o cadastro é do RH/DP e o único campo alterado aqui é a
      // marcação de competências (rota própria, que grava antes na fonte).
      const r = await api.colaboradores.salvarSkills(editing.id, String(data.skills ?? ""));
      setAvisoSync({ ok: true, texto: r.mensagem });
    } else if (editing) {
      await api.update(tab.resource, editing.id, data);
    } else {
      await api.create(tab.resource, data);
    }
    setFormOpen(false);
    await load();
    onDataChanged();
  }

  async function refreshAll() {
    await load();
    onDataChanged();
  }

  /** Puxa o cadastro do RH/DP para esta base (aba espelhada). */
  async function handleSincronizar() {
    setSincronizando(true);
    setAvisoSync(null);
    try {
      const r = await api.sincronizarColaboradores();
      setAvisoSync({ ok: r.ok, texto: r.mensagem });
      setForaDaEquipe(r.naoSaoDeCampo ?? []);
      setVerForaDaEquipe(false);
      // Quem ficou sem correspondência só existe aqui: oferece o envio ao RH/DP.
      setPendentesRh(r.ok ? r.somenteLocais : 0);
      setForaDaFonte(r.ok ? (r.foraDaFonte ?? []).map((c) => ({ id: c.id, nome: c.nome })) : []);
      await refreshAll();
    } catch (err) {
      setAvisoSync({
        ok: false,
        texto: err instanceof Error ? err.message : "Não foi possível sincronizar com o RH/DP.",
      });
    } finally {
      setSincronizando(false);
    }
  }

  /**
   * Envia ao RH/DP quem só existe aqui. Sem isso, um cadastro feito neste
   * sistema antes da centralização nunca casa com a fonte — e a Equipe fica
   * repetindo "N sem correspondência" sem oferecer saída.
   */
  async function handlePromover() {
    if (
      !confirm(
        "Enviar ao RH/DP os cadastros que só existem aqui?\n\n" +
          "Quem já estiver lá NÃO é duplicado — apenas vinculado. " +
          "Depois disso, o RH/DP passa a ser o dono do cadastro e as alterações descem para cá.",
      )
    ) {
      return;
    }
    setPromovendo(true);
    setAvisoSync(null);
    try {
      const r = await api.promoverColaboradores();
      setAvisoSync({ ok: r.ok, texto: r.mensagem });
      if (r.ok) {
        // Já traz de volta o cadastro central, agora vinculado.
        const s = await api.sincronizarColaboradores().catch(() => null);
        if (s) setPendentesRh(s.somenteLocais);
        await refreshAll();
      }
    } catch (err) {
      setAvisoSync({
        ok: false,
        texto: err instanceof Error ? err.message : "Não foi possível enviar ao RH/DP.",
      });
    } finally {
      setPromovendo(false);
    }
  }

  async function handleDelete(row: Row) {
    if (!confirm("Tem certeza que deseja excluir este registro?")) return;
    try {
      await api.remove(tab.resource, row.id);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  /**
   * Tira uma pessoa da lista da Equipe.
   *
   * A lista é espelho do RH/DP: quem continua cadastrado lá voltaria na próxima
   * sincronização, então o servidor recusa e explica o caminho (desligar na
   * fonte). Sai daqui só quem já não existe mais no cadastro central — o caso
   * de quem foi removido do RH e ficou sobrando nesta lista.
   */
  async function handleRemoverDaEquipe(row: Row) {
    const nome = String(row.nome ?? "este colaborador");
    if (
      !confirm(
        `Remover ${nome} da lista da equipe?\n\n` +
          "Isto só funciona para quem NÃO está mais cadastrado no RH/DP. " +
          "A pessoa sai também do Planejamento e do app do colaborador (a base é a mesma), " +
          "junto com as alocações de agenda dela.",
      )
    ) {
      return;
    }
    setRemovendo(true);
    setAvisoSync(null);
    try {
      const r = await api.colaboradores.removerDaEquipe(row.id);
      setAvisoSync({ ok: r.ok, texto: r.mensagem });
      setForaDaFonte((atual) => atual.filter((c) => c.id !== row.id));
      await refreshAll();
    } catch (err) {
      setAvisoSync({
        ok: false,
        texto: err instanceof Error ? err.message : "Não foi possível remover da equipe.",
      });
    } finally {
      setRemovendo(false);
    }
  }

  /**
   * Limpa de uma vez todos os que saíram do RH/DP e ficaram sobrando aqui.
   *
   * A confirmação lista os NOMES: "não está no RH/DP" inclui tanto o cadastro
   * de exemplo quanto gente de verdade que nunca foi cadastrada lá — quem
   * confirma precisa ver de quem se trata antes, não só o número.
   */
  async function handleLimparForaDaFonte() {
    const nomes = ordenarPorNome(foraDaFonte, (c) => c.nome)
      .map((c) => `• ${c.nome ?? `#${c.id}`}`)
      .join("\n");
    if (
      !confirm(
        `Remover da equipe ${foraDaFonte.length} pessoa(s) que não estão no cadastro do RH/DP?\n\n` +
          `${nomes}\n\n` +
          "CONFIRA A LISTA: quem é da casa mas nunca foi cadastrado no RH/DP também aparece aqui — " +
          'nesse caso, cadastre a pessoa no RH/DP (ou use "Enviar ao RH/DP") em vez de remover.\n\n' +
          "Quem for removido sai também do Planejamento e do app do colaborador (a base é a mesma), " +
          "junto com as alocações de agenda.",
      )
    ) {
      return;
    }
    setRemovendo(true);
    setAvisoSync(null);
    try {
      const r = await api.colaboradores.limparForaDaFonte();
      setAvisoSync({ ok: r.ok, texto: r.mensagem });
      setForaDaFonte([]);
      setPendentesRh(0);
      await refreshAll();
    } catch (err) {
      setAvisoSync({
        ok: false,
        texto: err instanceof Error ? err.message : "Não foi possível limpar a lista.",
      });
    } finally {
      setRemovendo(false);
    }
  }

  if (error)
    return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <span className="badge">{filtered.length} registro(s)</span>
          {tab.fonteRh ? (
            // Cadastro centralizado no RH/DP: aqui não se cria colaborador,
            // só se traz o que já foi cadastrado lá.
            <>
              {pendentesRh > 0 && (
                <button
                  className="btn-secondary"
                  onClick={handlePromover}
                  disabled={promovendo || sincronizando || removendo}
                  title="Cadastra no RH/DP quem só existe neste sistema e amarra o vínculo"
                >
                  {promovendo ? "Enviando..." : `Enviar ${pendentesRh} ao RH/DP`}
                </button>
              )}
              {podeExcluir && foraDaFonte.length > 0 && (
                <button
                  className="btn-secondary"
                  onClick={handleLimparForaDaFonte}
                  disabled={removendo || sincronizando || promovendo}
                  title="Tira da lista quem não está mais cadastrado no RH/DP"
                >
                  {removendo ? "Removendo..." : `Remover ${foraDaFonte.length} que saiu do RH/DP`}
                </button>
              )}
              <button className="btn-primary" onClick={handleSincronizar} disabled={sincronizando || promovendo}>
                <IconRefresh size={17} /> {sincronizando ? "Sincronizando..." : "Sincronizar com RH/DP"}
              </button>
            </>
          ) : (
            <button className="btn-primary" onClick={openNew}>
              <IconPlus size={17} /> Novo
            </button>
          )}
        </div>
      </div>

      {tab.fonteRh && (
        <div className="crud-aviso">
          O cadastro de colaboradores é feito no <strong>RH</strong> ou no <strong>DP</strong> e
          desce automaticamente para cá. Aqui você <strong>marca as competências</strong> de cada
          um (botão do lápis) — a marcação sobe para o cadastro central e vale para todo o portal.
          {/* A instrução da lixeira só faz sentido para quem a enxerga: excluir é
              de administrador, e mandar o usuário comum procurar um botão que não
              existe na tela dele é pior do que não dizer nada. */}
          {podeExcluir && (
            <>
              {" "}
              Para <strong>tirar alguém da lista</strong>, desligue a pessoa no RH/DP e sincronize:
              a lixeira remove daqui quem já não está mais no cadastro central.
            </>
          )}
        </div>
      )}

      {avisoSync && (
        <div className={`crud-aviso${avisoSync.ok ? "" : " crud-aviso-erro"}`}>
          {avisoSync.texto}
          {foraDaEquipe.length > 0 && (
            <button
              type="button"
              className="crud-aviso-link"
              onClick={() => setVerForaDaEquipe((v) => !v)}
            >
              {verForaDaEquipe ? "Ocultar" : `Ver quem ficou de fora (${foraDaEquipe.length})`}
            </button>
          )}
          {verForaDaEquipe && (
            <div className="crud-fora">
              <p>
                Estas pessoas estão cadastradas no RH/DP mas <strong>não entram neste sistema</strong>,
                porque o cargo delas não é de equipe de campo. Para trazer alguém para cá, corrija o
                cargo (ou o departamento) no cadastro do RH/DP e sincronize de novo.
              </p>
              <ul>
                {foraDaEquipeOrdenado.map((p) => (
                  <li key={`${p.nome}-${p.motivo}`}>
                    <strong>{p.nome}</strong> — {p.motivo}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty">Nenhum registro encontrado.</div>
        ) : (
          <table className={`table${tab.compact ? " table-compact" : ""}`}>
            <thead>
              <tr>
                {tab.fields.filter((f) => !f.hideInTable).map((f) => (
                  <th key={f.name}>{f.label}</th>
                ))}
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  {tab.fields.filter((f) => !f.hideInTable).map((f) => {
                    const text = formatCell(f, row);
                    return (
                      <td key={f.name}>
                        {f.type === "status" && text !== "—" ? (
                          <span className={`pill pill-${statusTone(text)}`}>{text}</span>
                        ) : (
                          text
                        )}
                      </td>
                    );
                  })}
                  <td className="col-actions">
                    {(tab.acoesLinha ?? []).map((a) => {
                      const meta = ACAO_META[a];
                      const Icon = meta.Icon;
                      return (
                        <button
                          key={a}
                          className={`icon-btn ${meta.cls}`}
                          title={meta.title}
                          onClick={() => setAcao({ tipo: a, row })}
                        >
                          <Icon size={17} />
                        </button>
                      );
                    })}
                    <button
                      className="icon-btn"
                      title={tab.fonteRh ? "Marcar competências do colaborador" : "Editar"}
                      onClick={() => openEdit(row)}
                    >
                      <IconEdit size={17} />
                    </button>
                    {/* Aba espelhada: o cadastro é do RH/DP, então a lixeira não
                        "exclui o colaborador" — ela tira da lista quem já não
                        está mais lá (quem está, o servidor recusa e explica).
                        Nos dois casos a lixeira só existe para administrador. */}
                    {podeExcluir &&
                      (tab.fonteRh ? (
                        <button
                          className="icon-btn icon-btn-danger"
                          title={
                            foraDaFonte.some((c) => c.id === row.id)
                              ? "Remover da equipe (não está no cadastro do RH/DP)"
                              : "Remover da equipe — só sai quem já não está no cadastro do RH/DP"
                          }
                          disabled={removendo}
                          onClick={() => handleRemoverDaEquipe(row)}
                        >
                          <IconTrash size={17} />
                        </button>
                      ) : (
                        <button
                          className="icon-btn icon-btn-danger"
                          title="Excluir"
                          onClick={() => handleDelete(row)}
                        >
                          <IconTrash size={17} />
                        </button>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Liberações: "Novo" e o lápis abrem o formulário próprio — é ele que
          busca a OS no Relatórios e monta o pedido ao Almoxarifado. */}
      {formOpen && tab.formCustom === "liberacao" && (
        <LiberacaoModal
          refs={refs}
          liberacao={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            void refreshAll();
          }}
        />
      )}

      {/* Equipamentos de Ensaio: o "Novo" é o cadastro completo das subabas
          (nome, marca, modelo, série, código de barras e certificado), o mesmo
          pop-up do Laboratório. Ele NÃO fecha sozinho depois de gravar — mostra
          a etiqueta para imprimir —, então cada gravação só recarrega a lista. */}
      {formOpen && !editing && tab.formNovo === "equipamento_ensaio" && (
        <FormEquipamento
          modo="novo"
          onFechar={() => setFormOpen(false)}
          onSalvo={() => void refreshAll()}
        />
      )}

      {formOpen && !tab.formCustom && (editing || !tab.formNovo) && (
        <RecordForm
          title={
            tab.fonteRh
              ? `Competências — ${String(editing?.nome ?? "colaborador")}`
              : editing
                ? `Editar — ${tab.label}`
                : `Novo — ${tab.label}`
          }
          fields={tab.fields}
          initial={editing}
          refs={refs}
          bloquearOrigemRh={tab.fonteRh}
          somenteLeitura={tab.fonteRh}
          enviarSoAlterados={!!tab.salvarSoAlterados && !!editing}
          onCancel={() => setFormOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      {acao?.tipo === "treinoIa" && (
        <SugerirTreinosModal
          colaborador={acao.row}
          onClose={() => setAcao(null)}
          onApplied={refreshAll}
        />
      )}
      {acao?.tipo === "pasta" && (
        <PastaModal colaborador={acao.row} onClose={() => setAcao(null)} />
      )}
      {acao?.tipo === "concluir" && (
        <ConcluirTreinoModal
          treinamento={acao.row}
          onClose={() => setAcao(null)}
          onConcluded={refreshAll}
        />
      )}
      {acao?.tipo === "certificado" && (
        <CertificadoModal
          treinamento={acao.row}
          onClose={() => setAcao(null)}
          onSent={refreshAll}
        />
      )}
      {acao?.tipo === "calibracao" && (
        <CertificadoCalibracaoModal
          equipamento={acao.row}
          onClose={() => setAcao(null)}
          onSaved={() => {
            setAcao(null);
            void refreshAll();
          }}
        />
      )}
      {acao?.tipo === "documentos" && (
        <DocumentosVeiculoModal veiculo={acao.row} onClose={() => setAcao(null)} />
      )}
      {acao?.tipo === "liberar" && (
        <LiberacaoModal
          refs={refs}
          liberacao={acao.row}
          onClose={() => setAcao(null)}
          onSaved={() => {
            setAcao(null); // fecha o modal ao salvar (senão fica aberto sem feedback)
            void refreshAll();
          }}
        />
      )}
    </div>
  );
}
